import { Box, Stack, useTheme } from '@mui/material';
import { FormikProps, useFormikContext } from 'formik';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'theSource/components/Button';
import { Spacer } from 'theSource/components/Spacer';
import { ActionWrapperStyled } from '../../FormStyled';
import { FormValues } from 'theSource/types';
import { FormProps } from '../..';

interface InnerFormProps<T extends object>
  extends Omit<
    FormProps<T>,
    | 'initialValues'
    | 'onSubmitClick'
    | 'validationSchema'
    | 'validateOnChange'
    | 'validateOnBlur'
    | 'formRef'
  > {
  formikProps: FormikProps<T>;
}

export const InnerForm = <T extends object>({
  children,
  childrenStackSpacing = 8,
  onCancelClick,
  testId,
  submitButtonLabel,
  submitButtonUseFullWidth = false,
  onChange,
  isLoading,
  formikProps,
  disableSubmitIfInvalid = true,
  disableSubmitButton = false,
}: InnerFormProps<T>) => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const { values, isValid, dirty, isSubmitting } = useFormikContext();

  useEffect(() => {
    if (onChange) {
      onChange(values as FormValues);
    }
  }, [values, onChange]);

  return (
    <Box
      data-testid={testId || 'form'}
      component="form"
      onSubmit={formikProps.handleSubmit}
      noValidate
    >
      <Stack spacing={theme.spacing(childrenStackSpacing)}>{children}</Stack>
      <Spacer height={5} />
      <ActionWrapperStyled>
        {onCancelClick && (
          <>
            <Button
              data-testid="cancel-button"
              disabled={isLoading}
              label={t('cancel')}
              variant="tertiary"
              type="button"
              onClick={onCancelClick}
            />
            <Spacer width={6} />
          </>
        )}
        <Button
          fullWidth={submitButtonUseFullWidth}
          data-testid="save-button"
          label={submitButtonLabel ?? t('save')}
          isLoading={isLoading}
          variant="primary"
          type="submit"
          disabled={
            disableSubmitButton ||
            isSubmitting ||
            (disableSubmitIfInvalid && (!isValid || (isValid && !dirty)))
          }
        />
      </ActionWrapperStyled>
    </Box>
  );
};
